import type { RealEstatePlanCode } from '@crm/shared/frontend';

export const CRM_MARKETING_SITE_URL = 'https://crm.inmueble.red';
export const MARKETPLACE_SITE_URL = 'https://inmueble.red';

export type SegmentCode =
  | 'agentOwner'
  | 'agencyPromoter'
  | 'developer'
  | 'financialDeveloper';

export type LegacyProductRouteKey =
  | 'freePublishing'
  | 'companies'
  | 'realEstateCrm'
  | 'collections';

export type MarketingRouteKind =
  | 'home'
  | 'segment'
  | 'legacyProduct'
  | 'standard';

export interface SegmentMarketingRoute {
  kind: 'segment';
  segmentCode: SegmentCode;
  planCode: RealEstatePlanCode;
  path: string;
  navLabelKey: string;
  titleKey: string;
  metaDescriptionKey: string;
  badgeKey: string;
  primaryCtaKey: string;
  secondaryCtaKey: string;
  primaryCta: 'freeRegister' | 'demoForm';
  secondaryHref: string;
  showInHeader: boolean;
}

export interface LegacyProductMarketingRoute {
  kind: 'legacyProduct';
  routeKey: LegacyProductRouteKey;
  path: string;
  primaryHref: 'register';
  secondaryHref: string;
  translationKey: LegacyProductRouteKey;
}

export interface IndexedMarketingRoute {
  kind: MarketingRouteKind;
  path: string;
  sitemapGroup: 'crm' | 'marketplace';
}

export const segmentMarketingRoutes: readonly SegmentMarketingRoute[] = [
  {
    kind: 'segment',
    segmentCode: 'agentOwner',
    planCode: 'free',
    path: '/agente-inmobiliario-dueno-directo/',
    navLabelKey: 'segments:nav.agentOwner',
    titleKey: 'segments:segments.agentOwner.title',
    metaDescriptionKey: 'segments:segments.agentOwner.metaDescription',
    badgeKey: 'segments:segments.agentOwner.badge',
    primaryCtaKey: 'segments:segments.agentOwner.primaryCta',
    secondaryCtaKey: 'segments:segments.agentOwner.secondaryCta',
    primaryCta: 'freeRegister',
    secondaryHref: '/precios/',
    showInHeader: true,
  },
  {
    kind: 'segment',
    segmentCode: 'agencyPromoter',
    planCode: 'basic',
    path: '/inmobiliaria-promotora/',
    navLabelKey: 'segments:nav.agencyPromoter',
    titleKey: 'segments:segments.agencyPromoter.title',
    metaDescriptionKey: 'segments:segments.agencyPromoter.metaDescription',
    badgeKey: 'segments:segments.agencyPromoter.badge',
    primaryCtaKey: 'segments:segments.agencyPromoter.primaryCta',
    secondaryCtaKey: 'segments:segments.agencyPromoter.secondaryCta',
    primaryCta: 'demoForm',
    secondaryHref: '/precios/',
    showInHeader: true,
  },
  {
    kind: 'segment',
    segmentCode: 'developer',
    planCode: 'plus',
    path: '/desarrolladora/',
    navLabelKey: 'segments:nav.developer',
    titleKey: 'segments:segments.developer.title',
    metaDescriptionKey: 'segments:segments.developer.metaDescription',
    badgeKey: 'segments:segments.developer.badge',
    primaryCtaKey: 'segments:segments.developer.primaryCta',
    secondaryCtaKey: 'segments:segments.developer.secondaryCta',
    primaryCta: 'demoForm',
    secondaryHref: '/precios/',
    showInHeader: true,
  },
  {
    kind: 'segment',
    segmentCode: 'financialDeveloper',
    planCode: 'pro',
    path: '/desarrolladora-financiera/',
    navLabelKey: 'segments:nav.financialDeveloper',
    titleKey: 'segments:segments.financialDeveloper.title',
    metaDescriptionKey: 'segments:segments.financialDeveloper.metaDescription',
    badgeKey: 'segments:segments.financialDeveloper.badge',
    primaryCtaKey: 'segments:segments.financialDeveloper.primaryCta',
    secondaryCtaKey: 'segments:segments.financialDeveloper.secondaryCta',
    primaryCta: 'demoForm',
    secondaryHref: '/precios/',
    showInHeader: false,
  },
] as const;

export const legacyProductMarketingRoutes: readonly LegacyProductMarketingRoute[] =
  [
    {
      kind: 'legacyProduct',
      routeKey: 'freePublishing',
      path: '/publicar-inmuebles-gratis/',
      primaryHref: 'register',
      secondaryHref: `${MARKETPLACE_SITE_URL}/peru`,
      translationKey: 'freePublishing',
    },
    {
      kind: 'legacyProduct',
      routeKey: 'companies',
      path: '/empresas/',
      primaryHref: 'register',
      secondaryHref: '/crm-inmobiliario/',
      translationKey: 'companies',
    },
    {
      kind: 'legacyProduct',
      routeKey: 'realEstateCrm',
      path: '/crm-inmobiliario/',
      primaryHref: 'register',
      secondaryHref: '/precios/',
      translationKey: 'realEstateCrm',
    },
    {
      kind: 'legacyProduct',
      routeKey: 'collections',
      path: '/cobranza-inmobiliaria/',
      primaryHref: 'register',
      secondaryHref: '/crm-inmobiliario/',
      translationKey: 'collections',
    },
  ] as const;

export const standardMarketingRoutes: readonly IndexedMarketingRoute[] = [
  { kind: 'home', path: '/', sitemapGroup: 'crm' },
  { kind: 'standard', path: '/precios/', sitemapGroup: 'crm' },
  { kind: 'standard', path: '/modulos/', sitemapGroup: 'crm' },
  { kind: 'standard', path: '/ia/', sitemapGroup: 'crm' },
  { kind: 'standard', path: '/comparar/', sitemapGroup: 'crm' },
  { kind: 'standard', path: '/privacy', sitemapGroup: 'crm' },
  { kind: 'standard', path: '/terms', sitemapGroup: 'crm' },
] as const;

export const indexedMarketingRoutes: readonly IndexedMarketingRoute[] = [
  ...standardMarketingRoutes,
  ...legacyProductMarketingRoutes.map((route) => ({
    kind: route.kind,
    path: route.path,
    sitemapGroup: 'crm' as const,
  })),
  ...segmentMarketingRoutes.map((route) => ({
    kind: route.kind,
    path: route.path,
    sitemapGroup: 'crm' as const,
  })),
] as const;

export function toReactRoutePath(path: string): string {
  if (path === '/') {
    return '';
  }

  return path.replace(/^\/+|\/+$/g, '');
}

export function buildCrmMarketingUrl(path: string): string {
  return `${CRM_MARKETING_SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

export function findSegmentMarketingRoute(
  segmentCode: SegmentCode
): SegmentMarketingRoute {
  const route = segmentMarketingRoutes.find(
    (candidate) => candidate.segmentCode === segmentCode
  );

  if (!route) {
    throw new Error(`Unknown segment marketing route: ${segmentCode}`);
  }

  return route;
}

export function findLegacyProductMarketingRoute(
  routeKey: LegacyProductRouteKey
): LegacyProductMarketingRoute {
  const route = legacyProductMarketingRoutes.find(
    (candidate) => candidate.routeKey === routeKey
  );

  if (!route) {
    throw new Error(`Unknown legacy product marketing route: ${routeKey}`);
  }

  return route;
}
